/**
 * Per-row truth for an in-flight/terminal submission batch (GDN-3216),
 * derived from `batchProgress.items` — the server's own per-claim statuses.
 * Pure; the worklist renders from this and never invents a state.
 *
 * Structurally typed rather than bound to the generated union: the server
 * can grow a status before codegen catches up, and an unrecognized status
 * must degrade to "still moving", not a compile error.
 */

export interface ClaimBatchProgressItemLike {
  claim_id: string;
  status?: string | null;
}

export interface ClaimBatchRowState {
  /** The server's status string, verbatim. */
  status: string;
  /** Needs the biller's eyes: blocked / failed / outcome_unknown. */
  attention: boolean;
  /** Still moving (queued or actively submitting) — renders dimmed. */
  inFlight: boolean;
  /** Reached the clearinghouse. */
  sent: boolean;
}

const ATTENTION_STATUSES = new Set(['blocked', 'failed', 'outcome_unknown']);

export function deriveClaimBatchRowStates(
  items: readonly ClaimBatchProgressItemLike[] | undefined,
): Map<string, ClaimBatchRowState> {
  const map = new Map<string, ClaimBatchRowState>();
  for (const item of items ?? []) {
    if (!item.claim_id) continue;
    const status = item.status ?? 'queued';
    const attention = ATTENTION_STATUSES.has(status);
    const sent = status === 'submitted';
    map.set(item.claim_id, {
      attention,
      /* Anything that is neither done nor flagged is honestly "still
         moving" — including statuses this build has never heard of. */
      inFlight: !attention && !sent,
      sent,
      status,
    });
  }
  return map;
}
